// The legal pages, as content rather than markup.
//
// Written from what the product actually does — every category below maps
// to something in the codebase, not to a template. A privacy policy that
// describes an app you did not build is worse than none: it is a promise
// you will not keep.
//
// The [[double bracket]] placeholders these shipped with are all filled in
// as of 2026-09-01. The machinery that tracked them stays: anything added
// later in that form still renders in a colour you cannot miss and still
// fails `legal.test.tsx`, so a new blank cannot be published by accident.

export const LAST_UPDATED = 'September 1, 2026';

// One definition, used by the support page and the landing page. It was
// declared separately in Support.tsx, which is how a product ends up
// publishing two different addresses.
export const SUPPORT_EMAIL = 'info@toposds.com';

export interface Section {
  heading: string;
  body?: string[];
  bullets?: { term?: string; text: string }[];
}

export const PRIVACY_SECTIONS: Section[] = [
  {
    heading: 'Who this covers',
    body: [
      'TopoSDS is an operations platform for delivery contractors. It is bought by a company '
      + 'and used by that company’s owners, managers, drivers and maintenance staff.',
      'That means there are two kinds of data here, and they are treated differently. Data about '
      + 'YOU as a person — your name, your login, your phone — is yours. Data the company keeps '
      + 'about its operation — a driver qualification file, an accident record, an inspection '
      + 'report — belongs to the company that employs you, even when it describes you. This '
      + 'policy is written by Topo Smart Design & Solutions LLC, which operates TopoSDS as a service to those '
      + 'companies.',
    ],
  },
  {
    heading: 'What we collect',
    bullets: [
      { term: 'Your account', text: 'Name, email address, phone number, password (stored only as a hash — we cannot read it), and a profile photo if you add one.' },
      { term: 'Your role', text: 'Which company you belong to, your role in it, and which service area you work in.' },
      { term: 'Documents', text: 'Files uploaded by you or about you — licences, medical certificates, I-9 and W-4 forms, background checks, insurance, vehicle registrations and inspections — along with their issue and expiry dates.' },
      { term: 'Work you record', text: 'Tasks and workflow steps you complete, the photos and notes you attach as proof, and when you completed them. Reporting an accident also records the location and time you provide.' },
      { term: 'Messages', text: 'Messages and attachments you send in the app’s chat.' },
      { term: 'Vehicles', text: 'Unit numbers, VINs, plates and service areas for the company’s fleet.' },
      { term: 'Devices', text: 'A push notification token for each device you sign in on, so the app can reach you.' },
      { term: 'An audit trail', text: 'A record of significant actions — who filed, viewed, shared or deleted a document, who changed somebody’s role, who signed in and when — including the IP address the action came from.' },
    ],
  },
  {
    heading: 'What we do with it',
    body: [
      'We use it to run the service the company bought: to show the right people the right documents, '
      + 'to tell somebody a certificate is about to expire, to route a task to the person who has to do it, '
      + 'and to let a company answer the question "who saw this file, and when".',
    ],
    bullets: [
      { text: 'We do not sell personal information.' },
      { text: 'We do not use your data to train machine learning models.' },
      { text: 'We do not show advertising, and we do not share data with advertisers.' },
      { text: 'We do not use analytics that follow you across other apps or websites.' },
    ],
  },
  {
    heading: 'The audit trail, specifically',
    body: [
      'Some of what TopoSDS records exists so the company can prove what happened. If a contractor is '
      + 'ever asked who opened a driver’s medical file, "we do not record that" is not an answer that '
      + 'survives the question.',
      'Audit entries are append-only. Nothing edits or deletes one, including us, and including the '
      + 'person the entry is about. That is the property that makes the record worth keeping.',
    ],
  },
  {
    heading: 'Who else touches it',
    body: [
      'We use a small number of infrastructure providers to run the service. They process data on our '
      + 'instructions and are not permitted to use it for anything else.',
    ],
    bullets: [
      { term: 'Google (Firebase, Cloud Storage)', text: 'Sign-in, chat delivery and file storage.' },
      { term: 'MongoDB Atlas', text: 'The application database.' },
      { term: 'Twilio', text: 'Text messages, where the company has enabled them.' },
      { term: 'Resend', text: 'Email — invitations, password resets and digests.' },
      { term: 'Expo', text: 'Push notification delivery to phones.' },
      { term: 'Our hosting providers', text: 'The servers the application itself runs on.' },
    ],
  },
  {
    heading: 'How long it is kept',
    body: [
      'Documents are kept for the retention period that applies to that kind of document — for many '
      + 'driver and vehicle records that is set by federal rules and runs for years after somebody '
      + 'leaves or a vehicle is sold. A document you delete moves to Recently Deleted and can be '
      + 'restored for 30 days, after which it is permanently removed unless a legal hold applies to it.',
      'Audit entries are kept for the life of the company’s account.',
      'Your account details are kept while your account exists.',
    ],
  },
  {
    heading: 'Deleting your account',
    body: [
      'You can delete your account from inside the app: Settings → Delete my account. It is permanent '
      + 'and it is not reversible.',
      'Deleting your account removes your login, your name, email address and phone number, your '
      + 'profile photo, your registered devices, and your membership of every company you belong to.',
      'It does not remove the company’s records. Compliance documents held about you, the audit trail, '
      + 'and messages you sent in a conversation stay with the company, because the company is legally '
      + 'required to keep them and they are its records rather than your profile. The app tells you '
      + 'exactly this before you confirm.',
      'If you are the only owner of a company, you have to make somebody else an owner before you can '
      + 'delete your account — otherwise nobody would be able to reach that company’s files.',
    ],
  },
  {
    heading: 'Your rights',
    body: [
      'You can see and correct your own details in the app at any time. You can delete your account as '
      + 'described above. For anything else — a copy of your data, a question about what is held, or a '
      + 'complaint — write to info@toposds.com and we will respond within 30 days.',
      'For records the company holds about you as an employee or contractor, the company is the right '
      + 'place to ask; we will help them respond.',
    ],
  },
  {
    heading: 'Security',
    body: [
      'Traffic is encrypted in transit and files are encrypted at rest by our storage provider. Access '
      + 'to a document is decided per request against your role and the document’s sensitivity, so a '
      + 'change to your access takes effect on your very next action rather than whenever a link '
      + 'happens to expire. Documents are never handed out as public or shareable links.',
      'No system is perfect. If we discover a breach affecting your data, we will tell you and the '
      + 'affected company without undue delay.',
    ],
  },
  {
    heading: 'Children',
    body: [
      'TopoSDS is a workplace tool and is not intended for anyone under 18. We do not knowingly collect '
      + 'information from children.',
    ],
  },
  {
    heading: 'Changes',
    body: [
      'If we change this policy in a way that materially affects you, we will say so in the app before '
      + 'the change takes effect. The date at the top always reflects the current version.',
    ],
  },
  {
    heading: 'Contact',
    body: [
      'TopoSDS is operated by Topo Smart Design & Solutions LLC, 1177 Taji Ct, Herndon, VA 20170. '
      + 'Privacy questions: info@toposds.com.',
    ],
  },
];

export const TERMS_SECTIONS: Section[] = [
  {
    heading: 'The agreement',
    body: [
      'These terms are between Topo Smart Design & Solutions LLC ("we") and the company that subscribes to TopoSDS '
      + '("you"). If you are using TopoSDS because your employer gave you an account, your employer '
      + 'agreed to these terms and this describes the service they bought.',
    ],
  },
  {
    heading: 'What the service is',
    body: [
      'TopoSDS is software for running a delivery contracting operation: document and compliance '
      + 'management, guided workflows, tasks, messaging and reporting.',
      'It is a tool for keeping records and prompting people. It is not legal, tax, safety or '
      + 'compliance advice, and it does not make you compliant with any regulation on its own. You are '
      + 'responsible for what your operation does and for the accuracy of what you put into it.',
    ],
  },
  {
    heading: 'Accounts',
    bullets: [
      { text: 'You are responsible for who you invite and for what they do with the access you give them.' },
      { text: 'Keep credentials to yourself. Tell us promptly if you think an account has been compromised.' },
      { text: 'Accounts are for people, not for sharing. One login per person.' },
      { text: 'You must be 18 or older to use TopoSDS.' },
    ],
  },
  {
    heading: 'Your data',
    body: [
      'The documents, messages and records you put into TopoSDS remain yours. You grant us the '
      + 'permission we need to host, process and display them in order to run the service for you, and '
      + 'nothing more.',
      'We do not sell your data, use it for advertising, or use it to train machine learning models.',
      'While your subscription is active you can export your documents from the app at any time. On '
      + 'termination you have 30 days to export before we begin deleting, unless the law requires us to '
      + 'keep something longer.',
    ],
  },
  {
    heading: 'Acceptable use',
    bullets: [
      { text: 'Do not upload anything you do not have the right to store or share.' },
      { text: 'Do not use TopoSDS to harass anybody or to break the law.' },
      { text: 'Do not attempt to access another company’s data, probe the service for vulnerabilities without our written permission, or interfere with its operation.' },
      { text: 'Do not resell or white-label the service without a written agreement.' },
    ],
  },
  {
    heading: 'Payment',
    body: [
      'Fees, billing period and any trial are as set out in your order or subscription page. Fees are '
      + 'payable in advance and are non-refundable except where the law requires otherwise. We will give '
      + 'you at least 30 days’ notice before a price change takes effect.',
    ],
  },
  {
    heading: 'Availability',
    body: [
      'We work to keep TopoSDS available and we take backups, but we do not promise uninterrupted '
      + 'service. Planned maintenance will be announced in advance where we reasonably can.',
    ],
  },
  {
    heading: 'Ending it',
    body: [
      'You can cancel at any time, effective at the end of your current billing period. We may suspend '
      + 'or terminate an account for non-payment, for a serious breach of these terms, or where we are '
      + 'required to by law — with notice, except where the breach makes notice unreasonable.',
    ],
  },
  {
    heading: 'Warranties and liability',
    body: [
      'TopoSDS is provided "as is". To the fullest extent the law allows, we disclaim implied warranties '
      + 'of merchantability, fitness for a particular purpose and non-infringement.',
      'To the fullest extent the law allows, neither party is liable for indirect, incidental or '
      + 'consequential damages, and our total liability arising out of these terms is limited to the '
      + 'fees you paid in the twelve months before the claim.',
      'Nothing here limits liability that cannot be limited by law.',
    ],
  },
  {
    heading: 'Changes to these terms',
    body: [
      'We may update these terms. If a change materially affects you, we will give you notice in the '
      + 'app or by email before it takes effect. Continuing to use TopoSDS after that means you accept '
      + 'the new terms.',
    ],
  },
  {
    heading: 'Governing law',
    body: [
      'These terms are governed by the laws of the Commonwealth of Virginia, United States, without regard to its conflict '
      + 'of law rules. The courts of the Commonwealth of Virginia have exclusive jurisdiction.',
    ],
  },
  {
    heading: 'Contact',
    body: ['Topo Smart Design & Solutions LLC, 1177 Taji Ct, Herndon, VA 20170. Questions: info@toposds.com.'],
  },
];

/** Everything still to be filled in before these pages are published. */
export function unresolvedPlaceholders(): string[] {
  // Walk the strings rather than stringifying the whole structure —
  // JSON.stringify of an array of arrays opens with "[[", which the
  // placeholder pattern happily matched.
  const found = new Set<string>();
  const scan = (text: string) => {
    for (const match of text.match(/\[\[[^[\]]+\]\]/g) ?? []) found.add(match);
  };
  for (const section of [...PRIVACY_SECTIONS, ...TERMS_SECTIONS]) {
    scan(section.heading);
    (section.body ?? []).forEach(scan);
    (section.bullets ?? []).forEach((bullet) => {
      if (bullet.term) scan(bullet.term);
      scan(bullet.text);
    });
  }
  return Array.from(found);
}
